import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, DollarSign, FileText, LogOut, Radio, User, UsersRound } from "lucide-react";
import api, { PLATFORM_NAME, errMsg } from "@/lib/api";
import { isLive } from "@/lib/lifecycle";
import { DriverJobCard } from "@/components/DriverJobCard";
import { JobOfferAlarm } from "@/components/JobOfferAlarm";
import { useAuth } from "@/context/AuthContext";
import { HelpButton } from "@/components/HelpButton";
import { keepAwake, letSleep } from "@/lib/wakeLock";

const POLL_MS = 12000;
const SHARE_STAGES = ["en_route_pickup", "arrived_pickup", "onboard", "en_route_destination",
                      "arrived_destination"];
const CLOSED = ["completed", "payment_finalized", "declined", "no_show", "cancelled"];

const TABS = [
  { label: "Jobs", icon: Car, to: "/portal/driver", testId: "driver-tab-jobs", active: true },
  { label: "Manifests", icon: FileText, to: "/portal/driver/manifests", testId: "driver-manifests-link" },
  { label: "Earnings", icon: DollarSign, to: "/portal/driver/earnings", testId: "driver-earnings-link" },
  { label: "Community", icon: UsersRound, to: "/portal/driver/community", testId: "driver-community-link" },
  { label: "Account", icon: User, to: "/portal/driver/account", testId: "driver-account-link" },
];

export default function DriverPortal() {
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [sharing, setSharing] = useState(false);
  // A chauffeur who presses GPS on before setting off stays on — the stage rule only ever adds.
  const [manual, setManual] = useState(false);
  // A refused permission must not be retried in a loop by the stage rule.
  const [blocked, setBlocked] = useState(false);
  const [permissionHelp, setPermissionHelp] = useState(false);
  const [state, setStates] = useState({});
  const [atPickup, setAtPickup] = useState({});
  const watchRef = useRef(null);

  const load = useCallback(
    () => api.get("/driver/trips").then(({ data }) => setTrips(data.trips)).catch((e) => setError(errMsg(e))),
    []
  );
  useEffect(() => { load(); }, [load]);

  const open = trips.filter((t) => !CLOSED.includes(t.stage));
  // An offer sits at "assigned" until the chauffeur answers it.
  const offers = open.filter((t) => t.stage === "assigned");
  const activeTrip = open.find((t) => isLive(t.stage)) || open[0];
  const later = open.filter((t) => t.id !== activeTrip?.id);
  const closed = trips.filter((t) => CLOSED.includes(t.stage)).slice(0, 5);

  const pollState = useCallback(() => {
    const live = trips.filter((t) => isLive(t.stage));
    if (live.length === 0) { setStates({}); return; }
    Promise.all(live.map((t) => api.get(`/trips/${t.id}/state`).then(({ data }) => [t.id, data]).catch(() => null)))
      .then((pairs) => setStates(Object.fromEntries(pairs.filter(Boolean))));
  }, [trips]);

  useEffect(() => {
    pollState();
    const timer = setInterval(() => { pollState(); load(); }, POLL_MS);
    return () => clearInterval(timer);
  }, [pollState, load]);

  const advance = async (trip, stage) => {
    setBusy(`${trip.id}-${stage}`);
    setError("");
    try {
      await api.post(`/trips/${trip.id}/advance`, { stage });
      await load();
      pollState();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const declineTrip = async (trip) => {
    setBusy(`${trip.id}-declined`);
    setError("");
    try {
      await api.post(`/trips/${trip.id}/decline`);
      await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const stopShare = (clearManual = true) => {
    if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
    watchRef.current = null;
    letSleep();
    if (clearManual) setManual(false);
    setSharing(false);
  };

  const startShare = (trip) => {
    setError("");
    if (!trip) {
      setError("You have no trip assigned right now — location sharing starts once dispatch assigns you a trip.");
      return;
    }
    if (!navigator.geolocation) {
      setError("This browser cannot share location. Open the chauffeur app in Chrome or Safari.");
      return;
    }
    // A sleeping screen stops the GPS, so hold the screen awake for as long as we are sharing.
    keepAwake();
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setPermissionHelp(false);
        setError("");
        api.post(`/driver/trips/${trip.id}/location`, {
          lat: pos.coords.latitude, lng: pos.coords.longitude,
        }).then(({ data }) => setAtPickup((p) => ({ ...p, [trip.id]: data.at_pickup })))
          .catch(() => {});
      },
      (err) => {
        // A dropped fix or a timeout is transient — the watch is still good, so keep sharing and
        // only say so quietly. A refused permission is the one case worth stopping for.
        if (err.code !== 1) {
          setError("Your location is taking a moment to come through. Keep this screen open.");
          return;
        }
        stopShare();
        setError("Location is blocked for this site, so your passenger cannot see you on the map.");
        setPermissionHelp(true);
        setBlocked(true);
      },
      { enableHighAccuracy: true, maximumAge: 10000 }
    );
    setSharing(true);
  };

  const toggleShare = () => {
    if (sharing) { stopShare(); return; }
    setBlocked(false);
    setManual(true);
    startShare(activeTrip);
  };

  useEffect(() => () => {
    if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
    letSleep();
  }, []);

  // Tracking follows the job, not a button: it starts when the chauffeur sets off for the pickup
  // and stops the moment the trip is closed, so the passenger's map is live exactly when it matters.
  // Pressing GPS on by hand keeps it on through the earlier stages as well.
  const liveStage = activeTrip?.stage;
  useEffect(() => { setManual(false); setBlocked(false); }, [activeTrip?.id]);
  useEffect(() => {
    const shouldShare = !blocked && (SHARE_STAGES.includes(liveStage) || manual);
    if (shouldShare && !sharing && activeTrip) {
      startShare(activeTrip);
    } else if (!shouldShare && sharing) {
      stopShare();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveStage, sharing, manual, blocked, activeTrip?.id]);

  const decideStop = async (trip, index, decision) => {
    setBusy(`${trip.id}-stop-${index}`);
    try {
      await api.post(`/driver/trips/${trip.id}/stops/${index}/${decision}`);
      await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const stopWait = async (trip, index, action) => {
    setBusy(`${trip.id}-wait-${index}`);
    try {
      await api.post(`/driver/trips/${trip.id}/stop-wait`, { index, action });
      await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const cardProps = (t) => ({
    trip: t,
    tripState: state[t.id],
    nearPickup: atPickup[t.id] || (state[t.id]?.meters_to_pickup != null
      && state[t.id].meters_to_pickup <= (state[t.id].geofence_radius || 200)),
    busy,
    onAdvance: advance,
    onDecline: declineTrip,
    onStopWait: stopWait,
    onStopDecision: decideStop,
    onReload: load,
    onError: setError,
    stopApprovalMode: company?.stop_approval_mode,
  });

  return (
    <div data-testid="driver-portal" className="min-h-screen bg-ink-900 pb-28 text-slate-200">
      <JobOfferAlarm offers={offers} onAnswered={load} />
      <header className="glass-header sticky top-0 z-40 border-b border-ink-600">
        <div className="mx-auto flex h-16 max-w-lg items-center justify-between gap-3 px-4">
          <div className="min-w-0">
            <p className="truncate font-display text-base font-semibold text-slate-100">{company?.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-gold">Chauffeur app</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button data-testid="driver-share-location" onClick={toggleShare}
              className={`flex h-10 items-center gap-2 rounded-full border px-3 font-mono text-[10px] uppercase tracking-widest ${
                sharing ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
                        : "border-ink-600 text-slate-300"}`}>
              <Radio className={`h-3.5 w-3.5 ${sharing ? "animate-pulse" : ""}`} />
              {sharing ? "GPS live" : "GPS off"}
            </button>
            <button data-testid="driver-logout" aria-label="Sign out"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-ink-600 text-slate-400 hover:text-gold">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg space-y-4 px-4 py-5">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-white">
            {activeTrip ? "Your next job" : "No job right now"}
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            {user?.name} · {open.length} open {open.length === 1 ? "job" : "jobs"}
          </p>
        </div>

        {error && (
          <p data-testid="driver-error" className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
            {error}
          </p>
        )}
        {permissionHelp && (
          <div data-testid="driver-permission-help" className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-amber-300">How to turn location back on</p>
            <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-slate-300">
              <li><span className="text-slate-100">iPhone (Safari):</span> tap the <span className="font-mono">AA</span> icon in the address bar → Website Settings → Location → Allow. Also check Settings → Privacy → Location Services → Safari.</li>
              <li><span className="text-slate-100">Android (Chrome):</span> tap the lock icon next to the address → Permissions → Location → Allow.</li>
              <li><span className="text-slate-100">Installed app:</span> phone Settings → Apps → this app → Permissions → Location → Allow while using.</li>
            </ul>
            <button data-testid="driver-permission-retry" onClick={() => { setPermissionHelp(false); toggleShare(); }}
              className="mt-3 font-mono text-[10px] uppercase tracking-widest text-gold hover:underline">
              Try sharing again
            </button>
          </div>
        )}

        {trips.length === 0 && (
          <p data-testid="driver-empty" className="rounded-2xl border border-ink-600 bg-ink-800 p-8 text-center text-sm text-slate-500">
            No trips assigned to you yet.
          </p>
        )}

        {activeTrip && <DriverJobCard hero {...cardProps(activeTrip)} />}

        {later.length > 0 && (
          <>
            <p data-testid="driver-upcoming-heading" className="pt-2 font-mono text-[11px] uppercase tracking-widest text-slate-500">
              Coming up · {later.length}
            </p>
            {later.map((t) => <DriverJobCard key={t.id} {...cardProps(t)} />)}
          </>
        )}

        {closed.length > 0 && (
          <>
            <p data-testid="driver-closed-heading" className="pt-2 font-mono text-[11px] uppercase tracking-widest text-slate-500">
              Recently finished
            </p>
            {closed.map((t) => <DriverJobCard key={t.id} {...cardProps(t)} />)}
          </>
        )}

        {company?.show_powered_by && (
          <p data-testid="driver-portal-powered-by" className="pt-2 text-center font-mono text-[11px] text-slate-600">
            Powered by {PLATFORM_NAME}
          </p>
        )}
      </main>

      <nav data-testid="driver-tabbar"
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-ink-600 bg-ink-900/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-lg">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.label} data-testid={tab.testId} onClick={() => navigate(tab.to)}
                className={`flex flex-1 flex-col items-center gap-1 py-3 transition-colors ${
                  tab.active ? "text-gold" : "text-slate-400 hover:text-slate-200"}`}>
                <Icon className="h-5 w-5" />
                <span className="font-mono text-[10px] uppercase tracking-widest">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
      <HelpButton app="driver" />
    </div>
  );
}
